import { FormikProvider, useFormik } from 'formik';
import { useCallback, useEffect, useMemo } from 'react';
import * as yup from 'yup';
import IncidentTypeSelectPaginated from '@/modules/common/components/IncidentTypeSelectPaginate.tsx';
import ReportTypeSelect from '@/modules/common/components/ReportTypeSelect.tsx';
import { reportsFormats } from '@/modules/common/components/modals/CreateReportModal.tsx';
import { mapNameIdToSelectOption } from '@/modules/common/helpers/mapNameIdToSelectOption.ts';
import ClientsSelectPaginated from '@/modules/admin-dashboard/components/ClientsSelectPaginated.tsx';
import DepartmentsSelectPaginated from '@/modules/admin-dashboard/components/DepartmentsSelectPaginated.tsx';
import FormSelectPaginate from '@/modules/admin-dashboard/components/FormSelectPaginate.tsx';
import RegisterSelectPaginate from '@/modules/admin-dashboard/components/RegisterSelectPaginate.tsx';
import UsersSelectPaginated from '@/modules/admin-dashboard/components/UsersSelectPaginated.tsx';
import {
  useCreateReportTemplateMutation,
  useGetTemplateQuery,
  useUpdateReportTemplateMutation,
} from '@/modules/admin-dashboard/queries/reports-templates.ts';
import {
  BaseModalProps,
  Button,
  ModalContentWrapper,
  SelectButtonsNew,
  SelectPaginateOption,
  TextInputField,
} from 'ui';
import {
  CreateReportTemplateRequest,
  ReportFormat,
  ReportTemplateResponse,
  ReportType,
  TaskResult,
} from 'compliance-api';

const validationSchema = yup.object({
  template: yup.object({
    name: yup
      .string()
      .trim()
      .min(3, 'Name must be at least 3 characters')
      .max(100, 'Name must be at most 50 characters')
      .required('Name is required'),
  }),
});

const taskResultOptions = [
  { label: 'Completed', value: TaskResult.Completed },
  { label: 'Overdue', value: TaskResult.Due },
];

interface FormikValues {
  registers: SelectPaginateOption<number>[];
  users: SelectPaginateOption<number>[];
  forms: SelectPaginateOption<number>[];
  departments: SelectPaginateOption<number>[];
  incidents: SelectPaginateOption<number>[];
  client?: SelectPaginateOption<number>;
  template: CreateReportTemplateRequest;
}

const initialValues: FormikValues = {
  registers: [],
  users: [],
  forms: [],
  departments: [],
  incidents: [],
  client: undefined,
  template: {
    name: '',
    format: ReportFormat.Pdf,
    type: ReportType.IncidentReport,
    assignee_ids: [],
    form_ids: [],
    register_ids: [],
    incident_type_ids: [],
    client_id: undefined,
    status: undefined,
  },
};

interface ReportTemplateModalProps extends BaseModalProps {
  onSuccess?: (res: ReportTemplateResponse) => void;
  updatingTemplateId?: number;
  clientId?: number;
}

const ReportTemplateModal = ({
  open,
  updatingTemplateId,
  onSuccess,
  onClose,
  clientId,
}: ReportTemplateModalProps) => {
  const formik = useFormik<FormikValues>({
    initialValues: (() => {
      return {
        ...initialValues,
        template: {
          ...initialValues.template,
          client_id: clientId,
        },
      };
    })(),
    validationSchema,
    onSubmit: async values => {
      const res = updatingTemplateId
        ? await updateTemplate({
            ...values.template,
            id: updatingTemplateId,
          })
        : await createTemplate(values.template);
      // if (updatingTemplateId) {
      //   res = await updateTemplate({ ...values.template, id: updatingTemplateId });
      // } else {
      //   res = await createTemplate(values.template);
      // }

      onSuccess && onSuccess(res.report_template);
      onClose();
    },
  });

  const { mutateAsync: createTemplate, isLoading: isCreateTemplateLoading } =
    useCreateReportTemplateMutation();

  const { mutateAsync: updateTemplate, isLoading: isUpdateTemplateLoading } =
    useUpdateReportTemplateMutation();

  const { data: initialTemplate } = useGetTemplateQuery(
    updatingTemplateId || 1,
    !updatingTemplateId,
  );

  const isStatusSelectVisible = useMemo(
    () =>
      formik.values.template.type === ReportType.TaskActivityReport ||
      formik.values.template.type === ReportType.TaskResultsReport,
    [formik.values.template.type],
  );

  useEffect(() => {
    if (!isStatusSelectVisible) {
      formik.setFieldValue('template.status', undefined);
    }
  }, [isStatusSelectVisible, formik.setFieldValue]);

  useEffect(() => {
    if (initialTemplate) {
      formik.setValues({
        registers: mapNameIdToSelectOption(initialTemplate?.registers),
        users: (initialTemplate?.assignees || []).map(({ id, first_name, last_name }) => ({
          label: `${first_name} ${last_name}`,
          value: id,
        })),
        forms: mapNameIdToSelectOption(initialTemplate?.forms),
        departments: mapNameIdToSelectOption(initialTemplate?.departments),
        incidents: mapNameIdToSelectOption(initialTemplate?.incident_types),
        client: initialTemplate.report_template?.client?.name
          ? {
              value: initialTemplate.report_template?.client?.id,
              label: initialTemplate.report_template?.client?.name,
            }
          : undefined,
        template: {
          ...initialTemplate.report_template,
          client_id: initialTemplate.report_template?.client_id || undefined,
        },
      });
    }
  }, [initialTemplate]);

  const handleChangeType = useCallback(
    (option: ReportType | null) => {
      formik.setFieldValue('template.type', option);
      formik.setFieldError('type', '');

      if (option !== ReportType.IncidentReport) {
        formik.setFieldValue('template.incident_type_ids', []);
      }
    },
    [formik],
  );

  const handleChangeRegister = useCallback(
    (newValue: SelectPaginateOption<number>[]) => {
      formik.setFieldValue('registers', newValue);
      formik.setFieldValue(
        'template.register_ids',
        newValue.map(({ value }) => value),
      );
    },
    [formik],
  );

  const handleChangeAssignee = useCallback(
    (newValue: SelectPaginateOption<number>[]) => {
      formik.setFieldValue('users', newValue);
      formik.setFieldValue(
        'template.assignee_ids',
        newValue.map(({ value }) => value),
      );
    },
    [formik],
  );

  const handleChangeFormat = useCallback(
    (format: ReportFormat) => {
      formik.setFieldValue('template.format', format);
    },
    [formik],
  );

  const handleChangeDepartment = useCallback(
    (newValue: SelectPaginateOption<number>[]) => {
      formik.setFieldValue('departments', newValue);
      formik.setFieldValue(
        'template.department_ids',
        newValue.map(({ value }) => value),
      );
    },
    [formik],
  );

  const handleChangeIncidentType = useCallback(
    (newValue: SelectPaginateOption<number>[]) => {
      formik.setFieldValue('incidents', newValue);
      formik.setFieldValue(
        'template.incident_type_ids',
        newValue.map(({ value }) => value),
      );
    },
    [formik],
  );

  const handleChangeForm = useCallback(
    (newValue: SelectPaginateOption<number>[]) => {
      formik.setFieldValue('forms', newValue);
      formik.setFieldValue(
        'template.form_ids',
        newValue.map(({ value }) => value),
      );
    },
    [formik],
  );

  const handleChangeClient = useCallback(
    (newValue: SelectPaginateOption<number> | null) => {
      formik.setFieldValue('client', newValue);
      formik.setFieldValue('template.client_id', newValue?.value || null);
    },
    [formik],
  );

  return (
    <ModalContentWrapper
      title={updatingTemplateId ? 'Update template' : 'Add new template'}
      open={open}
      onClose={onClose}
    >
      <FormikProvider value={formik}>
        <form className="w-full" onSubmit={e => e.preventDefault()}>
          <div className="flex flex-col gap-8">
            <TextInputField
              name="template.name"
              label="Template name"
              placeholder="Enter template name..."
            />
            <ReportTypeSelect
              hideBatch
              excludeTypes={[ReportType.ContractsReport]}
              value={formik.values.template.type}
              onChange={handleChangeType}
            />
            {isStatusSelectVisible && (
              <SelectButtonsNew
                label="STATUS"
                value={formik.values.template.status}
                options={taskResultOptions}
                onChange={status =>
                  formik.setFieldValue(
                    'template.status',
                    formik.values.template.status === status ? undefined : status,
                  )
                }
              />
            )}
            <RegisterSelectPaginate
              enableCreation
              isMulti={true}
              label="Label (optional)"
              onChange={handleChangeRegister}
              value={formik.values.registers}
            />
            <UsersSelectPaginated
              enableCreation
              isMulti={true}
              onChange={handleChangeAssignee}
              label="User (optional)"
              value={formik.values.users}
            />
            <FormSelectPaginate
              isMulti={true}
              formFieldTitle="form_ids"
              label="Form (optional)"
              onChange={handleChangeForm}
              value={formik.values.forms}
            />
            <SelectButtonsNew
              label="FORMAT"
              value={formik.values.template.format}
              options={reportsFormats}
              onChange={handleChangeFormat}
            />
            <DepartmentsSelectPaginated
              enableCreation
              isMulti={true}
              handleSelectDepartments={handleChangeDepartment}
              label="Department (optional)"
              formFieldTitle="department_ids"
              value={formik.values.departments}
            />
            {formik.values.template.type === 'incident_report' && (
              <IncidentTypeSelectPaginated
                enableCreation
                isMulti={true}
                onChange={handleChangeIncidentType}
                label="Event Type (optional)"
                formFieldTitle="incident_type_ids"
                value={formik.values.incidents}
              />
            )}
            {!clientId && (
              <ClientsSelectPaginated
                isMulti={false}
                onChange={handleChangeClient}
                value={formik.values.client}
                isClearable={true}
              />
            )}
            <Button
              variant="primary"
              type="submit"
              onClick={formik.submitForm}
              loading={isCreateTemplateLoading || isUpdateTemplateLoading}
              disabled={isCreateTemplateLoading || isUpdateTemplateLoading}
            >
              {updatingTemplateId ? 'Update template' : 'Create template'}
            </Button>
          </div>
        </form>
      </FormikProvider>
    </ModalContentWrapper>
  );
};

export default ReportTemplateModal;
